import { useState } from 'react'
import { Download, Loader2, FileSpreadsheet, FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/shared/hooks/use-toast'
import { descargarBlob, useExportarFidelizacion } from '../hooks/useFidelizacion'
import { parseFidelizacionError } from '../hooks/useFidelizacionError'
import type { ExportFormato, ExportTipo } from '../types'

export interface ExportarMenuTipoOption {
  tipo: ExportTipo
  label: string
  /** Filtros extra propios de este tipo; se combinan sobre los filtros base. */
  filtros?: Record<string, string | number | boolean | undefined>
}

interface ExportarMenuProps {
  configId: number
  /** Lista de tipos disponibles para esta página. */
  tipos: ExportarMenuTipoOption[]
  /** Filtros activos que se reenvían como query params al upstream. */
  filtros?: Record<string, string | number | boolean | undefined>
  colorNombre: string
  onRateLimitChange?: (remaining: number | null) => void
}

const FORMATOS: { formato: ExportFormato; label: string; Icon: typeof FileSpreadsheet }[] = [
  { formato: 'xlsx', label: 'Excel (.xlsx)', Icon: FileSpreadsheet },
  { formato: 'csv', label: 'CSV (.csv)', Icon: FileText },
]

export function ExportarMenu({
  configId,
  tipos,
  filtros,
  colorNombre,
  onRateLimitChange,
}: ExportarMenuProps) {
  const exportar = useExportarFidelizacion()
  const { toast } = useToast()
  const [tooLargeOpen, setTooLargeOpen] = useState(false)

  const handleSelect = async (tipo: ExportTipo, formato: ExportFormato) => {
    try {
      const tipoFiltros = tipos.find((t) => t.tipo === tipo)?.filtros
      const params = { ...filtros, ...tipoFiltros }
      const result = await exportar.mutateAsync({ configId, tipo, formato, params })
      descargarBlob(result)
      onRateLimitChange?.(result.rateLimitRemaining)
      toast({
        title: 'Descarga lista',
        description: result.filename,
      })
    } catch (err) {
      const parsed = parseFidelizacionError(err)
      if (parsed.status === 413) {
        setTooLargeOpen(true)
        return
      }
      if (parsed.status === 429) {
        toast({
          title: parsed.title,
          description: parsed.retryAfter
            ? `Reintenta en ${parsed.retryAfter} segundos.`
            : parsed.message,
          variant: 'destructive',
        })
        return
      }
      toast({
        title: parsed.title,
        description: parsed.message,
        variant: 'destructive',
      })
    }
  }

  const triggerClass = `bg-${colorNombre}-700 hover:bg-${colorNombre}-800 text-white`

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" className={triggerClass} disabled={exportar.isPending}>
            {exportar.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Exportar
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Recurso</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {tipos.map(({ tipo, label }) => (
            <DropdownMenuSub key={tipo}>
              <DropdownMenuSubTrigger>{label}</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {FORMATOS.map(({ formato, label: fLabel, Icon }) => (
                  <DropdownMenuItem
                    key={formato}
                    onSelect={(event) => {
                      event.preventDefault()
                      void handleSelect(tipo, formato)
                    }}
                  >
                    <Icon className="h-4 w-4" />
                    {fLabel}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={tooLargeOpen} onOpenChange={setTooLargeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Export demasiado grande</DialogTitle>
            <DialogDescription>
              El upstream limita los exports a 100.000 filas. Aplica filtros más restrictivos
              (rango de fechas más corto, provincia específica, etc.) y reintenta.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTooLargeOpen(false)}>
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
